import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler, useFieldArray } from 'react-hook-form';
import sortBy from 'lodash/sortBy';
import reduce from 'lodash/reduce';
import { Circle, Note } from 'phosphor-react';
import { Tooltip } from 'react-tooltip';

import withReport from 'components/with-report';
import { JOB_CATEGORY_CONFIRMATION_STEP } from 'constants/report-steps';
import { CONTRACTOR_REPORT_TYPE } from 'constants/common';
import JobCategoryMapping from 'components/job-category-confirmation-page/job-category-mapping';
import Spinner from 'components/spinner';
import StepProgressBar from 'components/step-progress-bar';
import ProcessingModal from 'components/processing-modal';
import { getJobCategoryMapping } from 'api/ml';
import { generateReport } from 'api/reports';
import { createOrUpdateJobCategoryMapping } from 'api/job-category-mapping';
import { FormValues } from 'types/job-category-mapping';
import { navigateAndStoreNextStep } from 'utils/navigation';
import { Report } from 'graphql-api';
import GlossarySlideOver from 'components/glossary-slideover';

function JobCategoryConfirmationPage({ report }: { report: Report }): JSX.Element {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [unconfirmedJobCategories, setUnconfirmedJobCategories] = useState<string[]>([]);
  const [mappedPercentage, setMappedPercentage] = useState(0);
  const [showProcessingModal, setShowProcessingModal] = useState<boolean>(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>();
  const [isImportedJobMappingsComplete, setIsImportedJobMappingsComplete] =
    useState<boolean>(false);

  const { fields, replace } = useFieldArray({
    control,
    name: 'mappings'
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setShowProcessingModal(true);
    const processedData = reduce(
      data.mappings,
      (mappings, value) => ({ ...mappings, [value.jobTitle]: value.jobCategory.id }),
      {}
    );
    const result = await createOrUpdateJobCategoryMapping(processedData);
    if (result != null) {
      const updatedReport = await generateReport(report?.id);
      if (updatedReport != null) {
        await navigateAndStoreNextStep(updatedReport, JOB_CATEGORY_CONFIRMATION_STEP, navigate);
      } else {
        setShowProcessingModal(false);
        alert('Generate report unsuccessfully!');
      }
    } else {
      setShowProcessingModal(false);
      alert('Saved job mappings unsuccessfully!');
    }
  };

  useEffect(() => {
    void (async () => {
      const res = await getJobCategoryMapping(report?.id);
      if (res != null) {
        const { jobMappingResult, isImportedJobMappingsComplete: isComplete } = res;
        !isComplete &&
          setMappedPercentage(
            Math.round(
              (jobMappingResult.filter((jobTitle) => jobTitle.jobCategory.confidence === true)
                .length /
                jobMappingResult.length) *
                100
            )
          );
        setIsImportedJobMappingsComplete(isComplete);
        const sortedMappings = sortBy(jobMappingResult, (jobTitle) => {
          return jobTitle.jobCategory.confidence === null
            ? 1
            : !jobTitle.jobCategory.confidence
            ? 2
            : 3;
        });
        replace(sortedMappings);
        setUnconfirmedJobCategories(
          sortedMappings
            .filter((item) => item.jobCategory.confidence !== true)
            .map((item) => item.jobTitle)
        );
      }
      setIsLoading(false);
    })();
  }, []);

  const confirmJobCategory = (jobTitle: string): void => {
    setUnconfirmedJobCategories(unconfirmedJobCategories.filter((item) => item !== jobTitle));
  };

  const handleGlossaryClick = (): void => {
    setShowGlossary(true);
  };

  const jobCategoryGlossary = (
    <div
      className="flex justify-center text-sm leading-4 font-bold text-gray-800 cursor-pointer"
      onClick={handleGlossaryClick}>
      <Note size="1rem" weight="bold" className="inline mr-1" />
      Job category glossary
    </div>
  );
  return (
    <div className="max-w-6xl m-auto bg-white shadow mt-8 py-16 px-14">
      <StepProgressBar
        report={report}
        currentStep={JOB_CATEGORY_CONFIRMATION_STEP}
        rightElement={jobCategoryGlossary}
      />
      {!isLoading && (
        <>
          {isImportedJobMappingsComplete ? (
            <>
              <div className="text-2xl font-medium leading-6 text-gray-900">
                Review your uploaded titles and EEO-1 job categories
              </div>
              <div className="mb-8 justify-between flex">
                <div className="text-base text-gray-700 mt-2 w-3/4">
                  Please review your&nbsp;
                  {report.reportType !== CONTRACTOR_REPORT_TYPE ? 'employee' : 'contractor'} titles
                  and EEO-1 job categories which are required for your CA Pay Data Report. If
                  needed, make any last changes before continuing to generate your report.
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-medium leading-6 text-gray-900">
                {`We’ve smartly mapped ${mappedPercentage}% of titles to EEO-1 job categories`}
              </div>
              <div className="mb-8 justify-between flex">
                <div className="text-base text-gray-700 mt-2 w-3/4">
                  We smartly mapped your&nbsp;
                  {report.reportType !== CONTRACTOR_REPORT_TYPE ? 'employee' : 'contractor'} titles
                  to EEO-1 job categories which are required for your CA Pay Data Report. Help us
                  review and correct these matches to make sure your report is accurate.
                </div>
                <div className="text-xs font-semibold tracking-wider self-end flex">
                  <Circle
                    weight="fill"
                    className="inline text-red-500 self-center mr-2"
                    size={12}
                  />
                  = NEEDS REVIEW
                </div>
              </div>
            </>
          )}
        </>
      )}

      {isLoading ? (
        <div className="text-center py-16">
          <Spinner className="text-gray-500" size={48} />
        </div>
      ) : (
        <>
          <hr className="mb-8" />
          <form
            onSubmit={(...args) => {
              void handleSubmit(onSubmit)(...args);
            }}>
            <table className="w-3/4">
              <tbody>
                <tr>
                  <th></th>
                  <th className="text-gray-300 text-xs font-semibold text-left px-3 pb-2">
                    JOB TITLE
                  </th>
                  <th className="text-gray-300 text-xs font-semibold text-center pb-2 w-full">
                    MAPS TO...
                  </th>
                  <th className="text-gray-300 text-xs font-semibold text-center pb-2 w-64">
                    EEO-1 JOB CATEGORY
                  </th>
                </tr>
                <JobCategoryMapping
                  register={register}
                  errors={errors}
                  fields={fields}
                  unconfirmedJobCategories={unconfirmedJobCategories}
                  confirmJobCategory={confirmJobCategory}
                />
              </tbody>
            </table>
            <hr className="mt-6 mb-8" />
            <div className="text-right">
              <button
                className="btn-primary ml-4 disabled:opacity-50"
                type="submit"
                disabled={unconfirmedJobCategories.length > 0}>
                <span data-tooltip-id="disable-generate-report-tooltip" data-tooltip-offset={15}>
                  Generate my report
                </span>
              </button>
              <Tooltip
                id="disable-generate-report-tooltip"
                content={
                  unconfirmedJobCategories.length > 0
                    ? 'Select or confirm an EEO-1 job category for all job titles to continue'
                    : undefined
                }
                className="bg-gray-900 text-white w-56 rounded px-2 py-1 text-sm font-normal text-center opacity-100"
              />
            </div>
          </form>
        </>
      )}
      {showProcessingModal && <ProcessingModal header="Generating your report..." />}
      <GlossarySlideOver open={showGlossary} setOpen={setShowGlossary} />
    </div>
  );
}

export default withReport(JobCategoryConfirmationPage);
